import styled, {
  css,
  DefaultTheme,
  Interpolation,
  ThemedStyledProps,
} from 'styled-components';
import colors from '../../../helpers/colors';
import fonts from '../../../helpers/fonts';
import mediaQuery from '../../../helpers/mediaQuery';
import spacing from '../../../helpers/spacing';

export const MarketingPromoBadgeContainer = styled.div`
  display: flex;
  flex-flow: column;
  position: relative;
  user-select: none;
  z-index: 0;
`;

type MarketingPromoProps = {
  size: 'small' | 'large';
};

const sizedValue = <T>(large: T, small: T) => ({
  size,
}: ThemedStyledProps<MarketingPromoProps, DefaultTheme>): T =>
  size === 'large' ? large : small;

const sizedSpacing = (
  multiplier: number
): Interpolation<ThemedStyledProps<MarketingPromoProps, DefaultTheme>> =>
  sizedValue(spacing(multiplier), spacing(multiplier / 2));

const calloutTopLeftStyles = css`
  margin-bottom: ${sizedSpacing(-1)};
  left: 0;
  align-self: flex-start;

  ::before {
    left: 0;
    top: 50%;
  }

  ::after {
    left: 0;
    top: 100%;
    border-top-left-radius: 100%;
  }
`;

const calloutBottomRightStyles = css`
  margin-top: ${sizedSpacing(-1)};
  right: 0;
  align-self: flex-end;

  ::before {
    right: 0;
    bottom: 50%;
  }

  ::after {
    right: 0;
    bottom: 100%;
    border-bottom-right-radius: 100%;
  }
`;

const calloutShapeStyleByColSpan = (
  colSpan: number
): Interpolation<ThemedStyledProps<MarketingPromoProps, DefaultTheme>> => css`
  height: ${spacing(2 * colSpan)};
  padding: ${spacing(0.5 * colSpan)} ${spacing(1.25 * colSpan)};

  ::before {
    height: calc(50% + ${spacing(1.5 * colSpan)});
    width: ${spacing(1.5 * colSpan)};
  }

  ::after {
    height: ${spacing(1.5 * colSpan)};
    width: ${spacing(1.5 * colSpan)};
  }
`;

export const MarketingPromoBadgeCalloutShape = styled.div<MarketingPromoProps>`
  background-color: ${colors.core03};
  color: ${colors.neutral01};
  box-shadow: 0 2px 3px 0 rgba(0, 0, 0, 0.2);

  position: relative;
  z-index: 3;

  display: inline-flex;
  align-items: center;

  border-radius: 999px;
  text-align: center;

  ::before {
    content: '';
    background-color: inherit;
    position: absolute;
    z-index: 1;
  }

  ::after {
    content: '';
    background-color: ${colors.core02};
    position: absolute;
    z-index: 2;
  }

  ${({ size }) => calloutShapeStyleByColSpan(size === 'large' ? 4 : 2)}
  ${mediaQuery.tabletPortrait`
    ${calloutShapeStyleByColSpan(3)}
  `}
  ${mediaQuery.tabletLandscape`
    ${({ size }) => calloutShapeStyleByColSpan(size === 'large' ? 4 : 2)}
  `}

  ${({ size }) => (size === 'large' ? calloutTopLeftStyles : calloutBottomRightStyles)}
`;

const mainShapeStyleByColSpan = (
  colSpan: number
): Interpolation<ThemedStyledProps<MarketingPromoProps, DefaultTheme>> => css`
  height: ${spacing(3.5 * colSpan)};
  padding: ${spacing(1 * colSpan)} ${spacing(1.5 * colSpan)};
`;

export const MarketingPromoBadgeMainShape = styled.div<MarketingPromoProps>`
  background-color: ${colors.core02};
  & {
    color: ${colors.neutral08};
  }
  box-shadow: 0 2px 3px 0 rgba(0, 0, 0, 0.2);

  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;

  border-radius: 999px;
  margin: 0 auto 0 0;

  ${({ size }) => mainShapeStyleByColSpan(size === 'large' ? 4 : 2)}
  ${mediaQuery.tabletPortrait`
    ${mainShapeStyleByColSpan(3)}
  `}
  ${mediaQuery.tabletLandscape`
    ${({ size }) => mainShapeStyleByColSpan(size === 'large' ? 4 : 2)}
  `}
`;

const headingFont = css`
  ${fonts.headingLarge}

  & {
    font-size: ${sizedValue('6vw', 'calc(8.5px + 1.5vw)')};
    line-height: ${sizedValue('7vw', 'calc(10px + 1.8vw)')};

    ${mediaQuery.tabletPortrait`
      font-size: ${sizedValue('2.7vw', 'calc(9px + 1vw)')};
      line-height: ${sizedValue('3.5vw', 'calc(11px + 1.2vw)')};
    `}

    ${mediaQuery.tabletLandscape`
      font-size: ${sizedValue('2.4vw', '1.65vw')};
      line-height: ${sizedValue('3vw', '2.0vw')};
    `}

    ${mediaQuery.desktop`
      font-size: ${sizedValue('28px', '16px')};
      line-height: ${sizedValue('34px', '18px')};
    `}
  }
`;

export const MarketPromoBadgeCalloutText = styled.div<MarketingPromoProps>`
  ${headingFont}
  & {
    font-weight: bold;
  }
  margin: 0;
  z-index: 3;
`;

export const MarketPromoBadgeMainShapeText = styled.p<MarketingPromoProps>`
  ${headingFont}
  & {
    color: ${colors.neutral08};
  }
  margin: 0;
  z-index: 4;

  ${({ size }) => size === 'large' && 'text-align: center;'}
`;

export const IconWrapper = styled.span`
  z-index: 4;
  display: flex;

  margin-left: ${spacing(1)};
  margin-right: ${spacing(-1)};
  transition: transform 0.3s ease-out;
`;

export const BadgeLink = styled.a`
  display: block;
  position: relative;
  text-decoration: none;
  outline: none;

  &:hover,
  &:focus-visible {
    ${IconWrapper} {
      transform: translateX(${spacing(0.5)});
    }
  }

  margin: -1px;
  padding: 1px;
  border-radius: 8px;

  &:focus-visible {
    box-shadow: ${colors.core02} 0px 0px 0px 2px;
  }
`;
